import { cn } from '@/lib/cn';
import { forwardRef, type CSSProperties, type ForwardedRef } from 'react';
import {
  AUDIO_BAR_OFFSET_PROPERTY,
  AUDIO_BAR_REST_OFFSET_PROPERTY,
  audioBarHeightToOffset,
  restingAudioBarOffset,
} from './audio-bar-transform';
import type { AudioBarProps } from './audio-bar.types';

export const AudioBar = forwardRef<HTMLElement, AudioBarProps>(
  (
    { as = 'div', minHeight, maxHeight, reducedMotionHeight, className, capClassName },
    ref
  ) => {
    const range = { min: minHeight, max: maxHeight };
    const restingOffset = restingAudioBarOffset(range);
    const reducedMotionOffset = audioBarHeightToOffset(
      reducedMotionHeight ?? minHeight,
      range
    );
    const capStyle = { height: maxHeight };
    const capClasses = cn('absolute inset-x-0 rounded-full', capClassName);
    const caps = (
      <>
        <span className="pointer-events-none absolute inset-x-0 top-0 h-1/2 overflow-hidden">
          <span
            data-audio-bar-cap="top"
            className={cn(capClasses, 'top-0 translate-y-[var(--audio-bar-offset)]')}
            style={capStyle}
          />
        </span>
        <span className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 overflow-hidden">
          <span
            data-audio-bar-cap="bottom"
            className={cn(
              capClasses,
              'bottom-0 translate-y-[calc(var(--audio-bar-offset)*-1)]'
            )}
            style={capStyle}
          />
        </span>
      </>
    );
    const elementProps = {
      'data-audio-bar': true,
      className: cn(
        'relative',
        reducedMotionHeight !== undefined &&
          'motion-reduce:![--audio-bar-offset:var(--audio-bar-rest-offset)]',
        className
      ),
      style: {
        height: maxHeight,
        [AUDIO_BAR_OFFSET_PROPERTY]: restingOffset,
        [AUDIO_BAR_REST_OFFSET_PROPERTY]: reducedMotionOffset,
      } as CSSProperties,
    };

    if (as === 'span') {
      return (
        <span ref={ref as ForwardedRef<HTMLSpanElement>} {...elementProps}>
          {caps}
        </span>
      );
    }

    return (
      <div ref={ref as ForwardedRef<HTMLDivElement>} {...elementProps}>
        {caps}
      </div>
    );
  }
);

AudioBar.displayName = 'AudioBar';
